import React from "react";
import { Edit } from "react-admin";
import { useParams, useNavigate } from "react-router-dom";
import { Alert, Box, Stack, Typography } from "@mui/material";
import EventBus from "../../EventBus";
import { EventBusEventsEnum } from "../../enums/EventBusEventsEnum";
import { CustomSnackbarTypes } from "../Common/CustomSnackBar";
import CircularLoader from "../Common/CircularLoader";
import ReasonSubreasonService from "../../services/ReasonSubreasonServices";
import { ReasonSubReasonResponse } from "../../interfaces/ReasonSubReason/ReasonSubReasonResponse";
import { UpdateReasonSubReason } from "../../interfaces/ReasonSubReason/UpdateReasonSubReason";
import ReasonSubreasonFormFields, {
    ReasonSubreasonFormValue,
} from "./ReasonSubreasonFormFields";

const reasonSubreasonService = new ReasonSubreasonService();

const numericOrNull = (value: any): number | null => {
    if (value === null || value === undefined || value === "") return null;
    const num = Number(value);
    return Number.isNaN(num) ? null : num;
};

const toFormValue = (record: ReasonSubReasonResponse): ReasonSubreasonFormValue => ({
    reason: record.reason ?? "",
    subReason: record.subReason ?? "",
    clientId: record.clientId ?? "",
    module: record.module ?? "",
    actionType: record.actionType ?? "",
    isActive: record.isActive,
    isVisible: record.isVisible,
    shouldCreateEscalation: record.shouldCreateEscalation,
    markLeave: record.markLeave ?? record.markleave ?? false,
    refundPercentage: record.refundPercentage ?? "",
    cookPenaltyAmount: record.cookPenaltyAmount ?? record.markPenalty ?? "",
    internalRatingParamId: record.internalRatingParamId ?? "",
    trHi: record.trHi ?? "",
    trBn: record.trBn ?? "",
});

const EditReasonSubreason = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const [loading, setLoading] = React.useState<boolean>(true);
    const [saving, setSaving] = React.useState<boolean>(false);
    const [form, setForm] = React.useState<ReasonSubreasonFormValue | null>(null);
    const [error, setError] = React.useState<string | null>(null);

    React.useEffect(() => {
        if (!id) return;
        setLoading(true);
        reasonSubreasonService
            .getOneReasonSubreason(Number(id))
            .then((res) => {
                if (!res?.data) {
                    setError("Reason mapping not found");
                } else {
                    setForm(toFormValue(res.data));
                }
            })
            .catch(() => setError("Failed to fetch reason mapping"))
            .finally(() => setLoading(false));
    }, [id]);

    const update = (key: keyof ReasonSubreasonFormValue, value: any) => {
        setForm((prev) => (prev ? { ...prev, [key]: value } : prev));
    };

    const handleSubmit = async () => {
        if (!form || !id) return;
        const refundPercentageNum = numericOrNull(form.refundPercentage);
        if (refundPercentageNum !== null && (refundPercentageNum < 80 || refundPercentageNum > 100)) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Refund % must be between 80 and 100",
                type: CustomSnackbarTypes.ERROR,
            });
            return;
        }
        const payload: UpdateReasonSubReason = {
            reason: form.reason?.trim() || undefined,
            subReason: form.subReason?.trim() || undefined,
            clientId: numericOrNull(form.clientId) ?? undefined,
            module: form.module?.trim() || undefined,
            actionType: form.actionType?.trim() || undefined,
            isActive: form.isActive,
            isVisible: form.isVisible,
            shouldCreateEscalation: form.shouldCreateEscalation,
            markLeave: form.markLeave,
            refundPercentage: refundPercentageNum,
            cookPenaltyAmount: numericOrNull(form.cookPenaltyAmount),
            internalRatingParamId: numericOrNull(form.internalRatingParamId),
            trHi: form.trHi?.trim() || undefined,
            trBn: form.trBn?.trim() || undefined,
        };

        setSaving(true);
        try {
            await reasonSubreasonService.updateReasonSubreason(Number(id), payload);
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Reason / Sub-reason updated",
                type: CustomSnackbarTypes.SUCCESS,
            });
            navigate(`/ReasonSubreason/${id}/show`, { replace: true });
        } catch (err) {
            EventBus.emitEvent(EventBusEventsEnum.SNACKBAR_SHOW, {
                message: "Failed to update reason mapping",
                type: CustomSnackbarTypes.ERROR,
            });
        } finally {
            setSaving(false);
        }
    };

    return (
        <Edit title="Edit Reason / Sub-reason" resource="ReasonSubreason" id={id}>
            <Box sx={{ p: { xs: 1.5, sm: 2.5 } }}>
                {loading && <CircularLoader />}
                {error && (
                    <Alert severity="error" sx={{ mb: 2 }}>
                        {error}
                    </Alert>
                )}
                {!loading && form && (
                    <>
                        <Stack sx={{ mb: 2 }}>
                            <Typography variant="h6" sx={{ fontWeight: 600 }}>
                                Edit Reason / Sub-reason #{id}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                Update reason details, behaviour, translations and flags.
                            </Typography>
                        </Stack>
                        <ReasonSubreasonFormFields
                            mode="edit"
                            value={form}
                            onChange={update}
                            onSubmit={handleSubmit}
                            onCancel={() => navigate(`/ReasonSubreason/${id}/show`)}
                            submitting={saving}
                        />
                    </>
                )}
            </Box>
        </Edit>
    );
};

export default EditReasonSubreason;
